import React, { FC, useCallback, useEffect, useState } from 'react';
import { TokenComponentProps } from './index';
import * as actions from '../mfaTokensActions';
import TokenTextField from './TokenTextField';
import TokenActions from './TokenActions';
import { showAlert } from '@silverfort/sf-components';
import useUploadAzureAppCert from '../api/mutations/useUploadAzureAppCert';
import TokenUploadCertificateField from './TokenUploadCertificateField';
import { v4 as uuidv4 } from 'uuid';
import { ENTRA_ID_MFA_TOKEN_FIELDS } from '../../../common/fields';
import EntraIdConfigureAccessPopup from './EntraIdConfigureAccessPopup';
import { DEFAULT_VENDOR_TO_ID, SSO_APP_SOURCE_TYPE } from '@silverfort-nodelibs/common/utils/consts';

type DispatchFn = (action: AddAction | UpdateAction) => void;

interface AddAction {
    type: typeof actions.ADD_ENTRA_ID_MFA_TOKEN;
    payload: EntraIdFormState & { id: string; lastModified: number; source: string; vendorId: number };
}

interface UpdateAction {
    type: typeof actions.UPDATE_ENTRA_ID_MFA_TOKEN;
    payload: { token: EntraIdFormState & { lastModified: number }; id: string };
}

type EntraIdProps = TokenComponentProps<{}, DispatchFn>;

interface EntraIdFormState {
    applicationName: string;
    loginUrl: string;
    audience: string;
    issuer: string;
    certificate: string;
    certificateFileName: string;
}

const EntraId: FC<EntraIdProps> = ({ fields, dispatch, onSave, onCancel, editEntraIdToken }) => {
    const [popupOpen, setPopupOpen] = useState(false);
    const [form, setForm] = useState<EntraIdFormState>({
        applicationName: '',
        loginUrl: '',
        audience: '',
        issuer: '',
        certificate: '',
        certificateFileName: ''
    });

    const isSomeFieldsEmpty = Object.keys(form).some((key) => form[key as keyof EntraIdFormState] === '');

    const dispatchAddEntraIdToken = useCallback(
        (value: EntraIdFormState) => {
            return dispatch({
                type: actions.ADD_ENTRA_ID_MFA_TOKEN,
                payload: {
                    ...value,
                    lastModified: Date.now(),
                    id: uuidv4(),
                    source: SSO_APP_SOURCE_TYPE.MFA_TOKENS,
                    vendorId: DEFAULT_VENDOR_TO_ID.AZURE
                }
            });
        },
        [dispatch]
    );

    const dispatchUpdateEntraIdToken = useCallback(
        (value: EntraIdFormState) => {
            return dispatch({
                type: actions.UPDATE_ENTRA_ID_MFA_TOKEN,
                payload: { token: { ...value, lastModified: Date.now() }, id: editEntraIdToken! }
            });
        },
        [dispatch, editEntraIdToken]
    );

    const onUploadSuccess = (fileName: string, certData: string) => {
        setForm((prev) => ({ ...prev, certificate: certData, certificateFileName: fileName }));
    };
    const onUploadError = () => {
        showAlert('Failed to upload certificate', 'error');
    };

    const { uploadCertificateMutation, isLoading: isUploadingCertificate } = useUploadAzureAppCert({
        onSuccessCallback: onUploadSuccess,
        onErrorCallback: onUploadError
    });

    const handleChange = useCallback((field: keyof EntraIdFormState, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    }, []);

    const onUploadCertificate = (files: FileList | (File | null)[] | null) => {
        if (files && files[0]) {
            const file = files[0];
            uploadCertificateMutation(file);
        }
    };

    const handleSave = useCallback(() => {
        if (editEntraIdToken) {
            dispatchUpdateEntraIdToken(form);
            onSave();
            return;
        }
        setPopupOpen(true);
    }, [dispatchUpdateEntraIdToken, editEntraIdToken, form, onSave]);

    const handlePopupConfirm = useCallback(() => {
        setPopupOpen(false);
        dispatchAddEntraIdToken(form);
        onSave();
    }, [dispatchAddEntraIdToken, form, onSave]);

    const handlePopupCancel = useCallback(() => {
        setPopupOpen(false);
    }, []);

    useEffect(() => {
        if (editEntraIdToken) {
            const tokenDetails = fields[ENTRA_ID_MFA_TOKEN_FIELDS.ENTRA_ID_MFA_APPS.ID].value.tokens.find(
                (token) => token.id === editEntraIdToken
            );
            setForm(tokenDetails);
        }
    }, [editEntraIdToken, fields]);

    return (
        <>
            <TokenTextField
                label="App name"
                value={form.applicationName}
                placeholder="Enter name"
                isSecret={false}
                onChange={(value) => handleChange('applicationName', value)}
            />

            <TokenTextField
                label="Login URL"
                value={form.loginUrl}
                placeholder="Enter URL"
                isSecret={false}
                onChange={(value) => handleChange('loginUrl', value)}
            />

            <TokenTextField
                label="SAML Entity ID"
                value={form.audience}
                placeholder="Enter SAML Entity ID"
                isSecret={false}
                onChange={(value) => handleChange('audience', value)}
            />

            <TokenTextField
                label="SAML Azure ID"
                value={form.issuer}
                placeholder="Enter SAML Azure ID"
                isSecret={false}
                onChange={(value) => handleChange('issuer', value)}
            />

            <TokenUploadCertificateField
                label="Upload certificate"
                isUploadingCertificate={isUploadingCertificate}
                onUploadCertificate={onUploadCertificate}
                isCompleted={form.certificate !== ''}
                onRemove={() => {
                    handleChange('certificate', '');
                    handleChange('certificateFileName', '');
                }}
                fileName={form.certificateFileName}
            />

            <TokenActions
                onSave={handleSave}
                onCancel={onCancel}
                disabled={isUploadingCertificate}
                saveDisabled={isSomeFieldsEmpty}
            />
            <EntraIdConfigureAccessPopup
                open={popupOpen}
                handleConfirm={handlePopupConfirm}
                handleClose={handlePopupCancel}
            />
        </>
    );
};

export default EntraId;
